import { Fragment, ReactNode } from 'react'

import { ThProps, Tr, TrProps } from 'app/common/ui-components/table'

import { defaultRenderHeaderCell } from './default-render-header-cell'

export type CommonRenderHeaderRowProps<K extends string> = {
  headerRowData: Record<K, unknown>
  dataArrange: Array<K>
  headerRowIndex: number
  headerRowsCount: number
  renderHeaderCell?: (props: {
    columnKey: K
    headerRowData: Record<K, unknown>
    headerRowIndex: number
    headerCollumnIndex: number
    headerCellProps?: Partial<ThProps>
  }) => ReactNode
  headerCellProps?: Record<K, Partial<ThProps>>
  headerRowProps?: Partial<TrProps>
  stuck?: boolean
  shadow?: boolean
  settings?: Record<string, boolean>
}

export type CommonRenderHeaderRow<K extends string> = (
  props: CommonRenderHeaderRowProps<K>,
) => ReactNode

export const defaultRenderHeaderRow = <K extends string>({
  headerRowData,
  dataArrange,
  headerRowIndex,
  headerRowsCount,
  renderHeaderCell = defaultRenderHeaderCell,
  headerCellProps,
  headerRowProps,
  stuck,
  shadow,
  settings,
}: CommonRenderHeaderRowProps<K>) => {
  const stickyAvailable = stuck !== undefined && shadow !== undefined
  const stickyBottomProps = stickyAvailable
    ? {
        borderShadowBottom: shadow,
        borderTopCompensate: stuck,
        bgFilled: stuck,
      }
    : {}

  const commonProps = {
    borderBottom: true,
  }

  return (
    <Tr header headerHasBorderBottom={false} {...headerRowProps}>
      {dataArrange
        .filter((key) => (settings && settings[key] !== undefined ? settings[key] : true))
        .map((key, headerCollumnIndex) => {
          const cellProps = headerCellProps ? headerCellProps[key] : {}
          const isBottomCell =
            headerRowIndex === headerRowsCount - 1 || cellProps
              ? headerRowIndex + (cellProps.rowSpan || 1) - 1 === headerRowsCount - 1
              : false

          return (
            <Fragment key={key}>
              {renderHeaderCell({
                columnKey: key,
                headerRowData,
                headerRowIndex,
                headerCollumnIndex,
                headerCellProps: {
                  ...cellProps,
                  ...(isBottomCell ? stickyBottomProps : {}),
                  ...commonProps,
                },
              })}
            </Fragment>
          )
        })}
    </Tr>
  )
}
